import type { ExtendedWorkspace } from "@performyard/service-contracts";

import { toAvatarGroupEmployees } from "../../utils/participants";

/**
 * Resolve a user's display name for a meeting context.
 *
 * Active workspace participants are the source of truth. `participantNames` is
 * a fallback name map covering users who once participated (including
 * removed/deactivated ones) so we can still render their name.
 */
export const getParticipantName = (
	workspace: ExtendedWorkspace | undefined,
	participantNames: Record<string, string> | undefined,
	userId: string | undefined
): string | undefined => {
	if (!userId) return undefined;
	return workspace?.participants?.find((p) => p.userId === userId)?.name || participantNames?.[userId];
};

/**
 * Pick the userId that the notes switcher should select on first render.
 *
 * Rules (covering PY-12664):
 * - Admin (non-participant) view → meeting creator, so the admin sees real notes
 *   instead of an empty lookup against their own userId.
 * - Otherwise, if the current user is in the meeting roster → themselves.
 * - Otherwise (e.g. they were added to the workspace after this meeting elapsed,
 *   so meeting.participants does NOT include them) → first participant in
 *   alphabetical order, so the panel defaults to a real notes owner.
 */
export const resolveDefaultNotesParticipantId = (props: {
	userId: string;
	meetingCreatorId: string;
	isAdminView: boolean;
	participants: Array<{ userId: string; name?: string }>;
	workspace: ExtendedWorkspace | undefined;
	participantNames: Record<string, string> | undefined;
}): string => {
	const { userId, meetingCreatorId, isAdminView, participants, workspace, participantNames } = props;

	if (isAdminView) return meetingCreatorId;

	const isCurrentUserParticipant = participants.some((p) => p.userId === userId);
	if (isCurrentUserParticipant) return userId;

	const ordered = toAvatarGroupEmployees(participants, {
		resolveName: (id) => getParticipantName(workspace, participantNames, id),
	});
	return ordered[0]?._id ?? userId;
};
